import * as Sentry from "@sentry/react";

const IGNORED_MESSAGES = new Set([
  "ResizeObserver loop limit exceeded",
  "ResizeObserver loop completed with undelivered notifications"
]);

const IGNORED_PATTERNS = [
  /chunkloaderror/i,
  /loading chunk [\\d]+ failed/i,
  /loading css chunk [\\d]+ failed/i
];

const isIgnoredMessage = (message) => {
  if (!message) {
    return false;
  }
  if (IGNORED_MESSAGES.has(message)) {
    return true;
  }
  return IGNORED_PATTERNS.some((pattern) => pattern.test(message));
};

const extractMessage = (event, hint) => {
  const original = hint?.originalException;
  if (typeof original === "string") {
    return original;
  }
  if (original && typeof original.message === "string") {
    return original.message;
  }
  if (typeof event?.message === "string") {
    return event.message;
  }
  const exceptionValue = event?.exception?.values?.[0]?.value;
  if (typeof exceptionValue === "string") {
    return exceptionValue;
  }
  return "";
};

const sanitizeHeaders = (headers) => {
  if (!headers || typeof headers !== "object") {
    return headers;
  }
  const sanitized = { ...headers };
  for (const key of Object.keys(sanitized)) {
    const lower = key.toLowerCase();
    if (lower === "authorization" || lower === "cookie") {
      sanitized[key] = "[Filtered]";
    }
  }
  return sanitized;
};

export const initSentry = () => {
  const dsn = import.meta.env.VITE_SENTRY_DSN;
  if (!dsn) {
    return;
  }

  const tracingIntegration =
    typeof Sentry.browserTracingIntegration === "function"
      ? Sentry.browserTracingIntegration()
      : typeof Sentry.BrowserTracing === "function"
        ? new Sentry.BrowserTracing()
        : null;

  const integrations = tracingIntegration ? [tracingIntegration] : [];

  Sentry.init({
    dsn,
    environment: import.meta.env.VITE_SENTRY_ENVIRONMENT || "development",
    release: import.meta.env.VITE_SENTRY_RELEASE || undefined,
    integrations,
    tracesSampleRate: 0.1,
    beforeSend(event, hint) {
      const message = extractMessage(event, hint);
      if (isIgnoredMessage(message)) {
        return null;
      }
      if (event?.request?.headers) {
        return {
          ...event,
          request: {
            ...event.request,
            headers: sanitizeHeaders(event.request.headers)
          }
        };
      }
      return event;
    }
  });
};
